import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

const TIER_LABELS = {
  free:      'Free',
  trial:     '3-Day Free Trial',
  starter:   'Starter — $29.99/mo',
  pro:       'Pro — $49.99/mo',
  unlimited: 'Unlimited — $69.99/mo',
};

const TIER_COLOR = {
  free:      '#6b7280',
  trial:     '#f59e0b',
  starter:   '#3b82f6',
  pro:       '#8b5cf6',
  unlimited: '#10b981',
};

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub]                 = useState(null);
  const [loading, setLoading]         = useState(true);
  const [cancelling, setCancelling]   = useState(false);
  const [cancelMsg, setCancelMsg]     = useState('');
  const [legalOpen, setLegalOpen]     = useState(null); // 'privacy' | 'terms'
  const [subOpen, setSubOpen]         = useState(false);

  // ── Cancellation modal state ────────────────────────────────
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelStep, setCancelStep]   = useState(1);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelTyped, setCancelTyped]   = useState('');

  // ── Load subscription ───────────────────────────────────────
  const loadSub = () => {
    const token = localStorage.getItem('proparv_token');
    if (!token) { navigate('/login'); return; }
    fetch('/api/subscription/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { setSub(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadSub();
  }, []);

  // ── Cancel subscription ─────────────────────────────────────
  const openCancelModal = () => { setCancelModal(true); setCancelStep(1); setCancelReason(''); setCancelTyped(''); };
  const closeCancelModal = () => { setCancelModal(false); setCancelStep(1); setCancelReason(''); setCancelTyped(''); };

  const handleCancel = async () => {
    const token = localStorage.getItem('proparv_token');
    if (!token) return;
    setCancelling(true);
    setCancelMsg('');
    try {
      const r    = await fetch('/api/stripe/cancel', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (data.success) {
        setCancelMsg('Your subscription has been cancelled.');
        closeCancelModal();
        loadSub();
      } else {
        setCancelMsg(data.message || data.error || 'Could not cancel. Please try again.');
      }
    } catch {
      setCancelMsg('Network error. Please try again.');
    }
    setCancelling(false);
  };

  const tier    = sub?.tier || user?.subscription?.tier || 'free';
  const status  = sub?.status || user?.subscription?.status;
  const renewAt = sub?.renewsAt ? new Date(sub.renewsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null;
  const isActive = status === 'active' || status === 'trial';

  return (
    <div style={{ paddingTop: 84, minHeight: '100vh', background: 'var(--bg-dark)' }}>
      <div className="container" style={{ maxWidth: 680, paddingBottom: 80 }}>

        {/* Header */}
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>Settings</h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 32 }}>{user?.email}</p>


        {/* ── Account ───────────────────────────────────────── */}
        <section style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 14, padding: 28, marginBottom: 20
        }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
            Account
          </h2>
          <Row label="Name"  value={user?.name  || '—'} />
          <div style={{ height: 10 }} />
          <Row label="Email" value={user?.email || '—'} />
          <button
            onClick={() => { logout(); navigate('/'); }}
            style={{
              marginTop: 20, padding: '10px 20px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'transparent',
              color: 'var(--text-secondary)', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer'
            }}
          >
            Log Out
          </button>
        </section>

        {/* ── Legal ─────────────────────────────────────────── */}
        <section style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 14, padding: 28, marginBottom: 20
        }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
            Legal
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <LegalBtn label="Privacy Policy"    onClick={() => setLegalOpen('privacy')} />
            <LegalBtn label="Terms of Service"  onClick={() => setLegalOpen('terms')}   />
          </div>
        </section>

        {/* ── Subscription Card ─────────────────────────────── */}
        <section style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <button onClick={() => setSubOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Subscription</h2>
            <span style={{ color: 'var(--text-muted)', fontSize: '1rem', transition: 'transform 0.2s', transform: subOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
          </button>
          {subOpen && <div style={{ padding: '0 28px 28px' }}>
            {loading ? (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', color: 'var(--text-muted)' }}>
                <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Loading…
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${TIER_COLOR[tier]}22`, border: `1.5px solid ${TIER_COLOR[tier]}55`, fontSize: '1.4rem' }}>
                    {tier === 'free' ? '🔒' : tier === 'trial' ? '⏳' : tier === 'unlimited' ? '♾️' : tier === 'pro' ? '⚡' : '✅'}
                  </div>
                  <div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)' }}>{TIER_LABELS[tier] || tier}</div>
                    <div style={{ fontSize: '0.82rem', marginTop: 2 }}>
                      <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, background: isActive ? 'rgba(16,185,129,0.15)' : 'rgba(107,114,128,0.15)', color: isActive ? '#34d399' : '#9ca3af', fontWeight: 600, fontSize: '0.78rem' }}>
                        {status === 'cancelling' ? 'Cancelling' : isActive ? 'Active' : status || 'Inactive'}
                      </span>
                      {status === 'trial' && <span style={{ marginLeft: 10, color: '#f59e0b', fontSize: '0.8rem', fontWeight: 600 }}>Free trial — you won't be charged until day 3</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {renewAt && <Row label={status === 'cancelling' ? 'Access until' : 'Renews on'} value={renewAt} />}
                  {sub?.searchLimit != null && <Row label="Searches" value={sub.searchLimit === -1 ? 'Unlimited' : `${sub.searchesUsed || 0} / ${sub.searchLimit} used`} />}
                </div>
                <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {(isActive || status === 'cancelling') && tier !== 'free' && status !== 'cancelling' && (
                    <button onClick={openCancelModal} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.08)', color: '#fca5a5', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
                      Cancel Subscription
                    </button>
                  )}
                  {status === 'cancelling' && <div style={{ fontSize: '0.85rem', color: '#f59e0b', alignSelf: 'center' }}>Cancellation scheduled — access remains until billing period ends.</div>}
                  {(!isActive || tier === 'free') && (
                    <button onClick={() => navigate('/pricing')} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}>
                      Upgrade Plan
                    </button>
                  )}
                </div>
                {cancelMsg && (
                  <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, background: cancelMsg.includes('cancelled') ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: cancelMsg.includes('cancelled') ? '#34d399' : '#fca5a5', fontSize: '0.875rem', fontWeight: 600 }}>
                    {cancelMsg}
                  </div>
                )}
              </>
            )}
          </div>}
        </section>

      </div>

      {/* ── Cancel Subscription Modal ──────────────────────── */}
      {cancelModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%' }}>

            {/* Step 1 — What you'll lose */}
            {cancelStep === 1 && (
              <>
                <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>Before you go…</div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: 20 }}>Cancelling your subscription means you'll immediately lose access to:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
                  {['ARV + comps analysis on any property', 'AI deal scoring and strategy breakdowns', 'Full deal analysis PDF reports', 'Dashboard deal history'].map(item => (
                    <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.875rem', color: '#fca5a5' }}>
                      <span style={{ fontWeight: 700 }}>✕</span> {item}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={closeCancelModal} style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>Keep My Subscription</button>
                  <button onClick={() => setCancelStep(2)} style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>Continue Cancelling</button>
                </div>
              </>
            )}

            {/* Step 2 — Reason */}
            {cancelStep === 2 && (
              <>
                <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>Why are you leaving?</div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: 20 }}>Your feedback helps us improve ProPARV.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
                  {['Too expensive', 'Not using it enough', 'Missing features I need', 'Found a better tool', 'Just taking a break', 'Other'].map(reason => (
                    <label key={reason} onClick={() => setCancelReason(reason)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: `1px solid ${cancelReason === reason ? 'var(--primary)' : 'var(--border)'}`, background: cancelReason === reason ? 'rgba(37,99,235,0.1)' : 'transparent', cursor: 'pointer', fontSize: '0.875rem', color: cancelReason === reason ? 'var(--primary-light)' : 'var(--text-secondary)' }}>
                      <input type="radio" checked={cancelReason === reason} onChange={() => setCancelReason(reason)} style={{ accentColor: 'var(--primary)' }} />
                      {reason}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setCancelStep(1)} style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>← Back</button>
                  <button onClick={() => setCancelStep(3)} disabled={!cancelReason} style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: 'none', background: cancelReason ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.05)', color: cancelReason ? '#fca5a5' : 'var(--text-muted)', fontWeight: 700, fontSize: '0.9rem', cursor: cancelReason ? 'pointer' : 'not-allowed', border: '1px solid rgba(239,68,68,0.3)' }}>Continue</button>
                </div>
              </>
            )}

            {/* Step 3 — Type CANCEL */}
            {cancelStep === 3 && (
              <>
                <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fca5a5', marginBottom: 8 }}>Final confirmation</div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: 6 }}>This action is <strong style={{ color: 'var(--text-primary)' }}>permanent and immediate</strong>. You will lose access to all paid features right now.</p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: 20 }}>Type <strong style={{ color: '#fca5a5', letterSpacing: 2 }}>CANCEL</strong> below to confirm:</p>
                <input
                  className="input"
                  value={cancelTyped}
                  onChange={e => setCancelTyped(e.target.value)}
                  placeholder="Type CANCEL here"
                  style={{ width: '100%', marginBottom: 20, fontSize: '1rem', letterSpacing: 2, textAlign: 'center' }}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setCancelStep(2)} style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>← Back</button>
                  <button
                    onClick={handleCancel}
                    disabled={cancelTyped !== 'CANCEL' || cancelling}
                    style={{ flex: 1, padding: '11px 0', borderRadius: 8, border: 'none', background: cancelTyped === 'CANCEL' ? '#dc2626' : 'rgba(255,255,255,0.05)', color: cancelTyped === 'CANCEL' ? '#fff' : 'var(--text-muted)', fontWeight: 700, fontSize: '0.9rem', cursor: cancelTyped === 'CANCEL' && !cancelling ? 'pointer' : 'not-allowed' }}
                  >
                    {cancelling ? 'Cancelling…' : 'Confirm Cancel'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Legal Modal ────────────────────────────────────── */}
      {legalOpen && (
        <div
          onClick={() => setLegalOpen(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 16, padding: 32, maxWidth: 600, width: '100%',
              maxHeight: '80vh', overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
                {legalOpen === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
              </h3>
              <button
                onClick={() => setLegalOpen(null)}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.7 }}>
              {legalOpen === 'privacy' ? <PrivacyPolicy /> : <TermsOfService />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.875rem' }}>{value}</span>
    </div>
  );
}

function LegalBtn({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '13px 0', background: 'none', border: 'none', borderBottom: '1px solid var(--border)',
        color: 'var(--text-primary)', fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer', width: '100%'
      }}
    >
      {label}
      <span style={{ color: 'var(--text-muted)', fontSize: '1.1rem' }}>›</span>
    </button>
  );
}

function PrivacyPolicy() {
  return (
    <>
      <p><strong style={{ color: 'var(--text-primary)' }}>Last updated: {new Date().getFullYear()}</strong></p>
      <p>PROPARV ("we", "us") operates the PROPARV website and browser extension. This policy explains how we collect, use, and protect your information.</p>
      <p><strong style={{ color: 'var(--text-primary)' }}>Information We Collect</strong><br />
      We collect your name, email address, and payment information when you register or subscribe. We also collect property search queries to provide our analysis service.</p>
      <p><strong style={{ color: 'var(--text-primary)' }}>How We Use Your Information</strong><br />
      Your information is used to operate and improve PROPARV, process payments via Stripe, and provide real estate analysis results. We do not sell your personal data to third parties.</p>
      <p><strong style={{ color: 'var(--text-primary)' }}>Payments</strong><br />
      All payments are processed securely by Stripe. PROPARV does not store your credit card details.</p>
      <p><strong style={{ color: 'var(--text-primary)' }}>Cookies</strong><br />
      We use localStorage to store your session token. No third-party tracking cookies are used.</p>
      <p><strong style={{ color: 'var(--text-primary)' }}>Contact</strong><br />
      For privacy concerns, contact us at support@proparv.com.</p>
    </>
  );
}

function TermsOfService() {
  return (
    <>
      <p><strong style={{ color: 'var(--text-primary)' }}>Last updated: {new Date().getFullYear()}</strong></p>
      <p>By using PROPARV you agree to these terms. Please read them carefully.</p>
      <p><strong style={{ color: 'var(--text-primary)' }}>Service</strong><br />
      PROPARV provides AI-assisted real estate analysis tools including ARV estimates, rental projections, and deal scoring. All estimates are for informational purposes only and do not constitute financial or investment advice.</p>
      <p><strong style={{ color: 'var(--text-primary)' }}>Subscriptions & Free Trial</strong><br />
      Paid plans include a 3-day free trial. You will not be charged until the trial ends. You may cancel at any time before the trial ends to avoid being billed. After cancellation you retain access until the end of the current billing period.</p>
      <p><strong style={{ color: 'var(--text-primary)' }}>Acceptable Use</strong><br />
      You may not use PROPARV to scrape data, resell results, or violate any applicable laws. Accounts found in violation may be terminated without refund.</p>
      <p><strong style={{ color: 'var(--text-primary)' }}>Disclaimer</strong><br />
      PROPARV is provided "as is". We make no guarantees about the accuracy of property valuations. Always perform your own due diligence before making investment decisions.</p>
      <p><strong style={{ color: 'var(--text-primary)' }}>Contact</strong><br />
      For questions, contact us at support@proparv.com.</p>
    </>
  );
}
